import { Directive } from '@angular/core';
import { environment } from '@env/environment';
import { permissions } from 'shared/constants/permissions';
import { CurrentShijiUserInterface } from 'shared/interfaces/current-shiji-user.interface';
import { AccessLevel, PermissionJSON, PermissionObject } from '../interfaces/permissions.interface';
import { CurrentSupplierUserInterface, InternalPermissions, ResourcePermissions } from '../interfaces/current-supplier-user.interface';
import { PERMISSIONS_HIERARCHY } from '../constants/general-constants';
import { FeatureGroup } from '../interfaces/feature-group.interface';

@Directive()
export abstract class BasePermissionsFunctions {
    protected hasAccess(
        currentUser: CurrentSupplierUserInterface | CurrentShijiUserInterface,
        permissionObject: PermissionJSON | PermissionJSON[] | [PermissionJSON[]],
    ): boolean {
        let permissionCheckFunction: Function;

        if (this.isCurrentUserAShijiUser(currentUser)) {
            permissionCheckFunction = this.hasAccessBasedOnAdminPermission.bind(this);
        } else {
            permissionCheckFunction = this.hasAccessBasedOnSimplePermission.bind(this);
        }

        if (!Array.isArray(permissionObject)) {
            permissionObject = [permissionObject];
        }

        for (let i = 0; i < permissionObject.length; i++) {
            let permissionObjectElement = permissionObject[i];
            let hasOrAccess: any = false;

            if (!Array.isArray(permissionObjectElement)) {
                permissionObjectElement = [permissionObjectElement];
            }

            for (let j = 0; j < permissionObjectElement.length; j++) {
                const permissionObjectSubElement = permissionObjectElement[j];

                // Check actual permissions
                if (permissionCheckFunction(permissionObjectSubElement, currentUser)) {
                    hasOrAccess = true;
                    break;
                }
            }

            if (!hasOrAccess) {
                return false;
            }
        }

        return true;
    }

    protected isCurrentUserAShijiUser(
        currentUser: CurrentSupplierUserInterface | CurrentShijiUserInterface,
    ): currentUser is CurrentShijiUserInterface {
        return Boolean(currentUser.isShiji && (currentUser as CurrentShijiUserInterface).shijiPermissions);
    }

    /**
     * Used to check if the user has access to a certain area
     * @param permissionJSON
     * @param currentUser
     * @protected
     */
    protected hasAccessBasedOnSimplePermission(permissionJSON: PermissionJSON, currentUser: CurrentSupplierUserInterface): boolean {
        if (!permissionJSON.type) {
            if (environment.dev) {
                console.error('Permission type is not defined', permissionJSON);
            }
        }

        if (permissionJSON.type === 'admin') {
            return false;
        }

        const [currentUserPermissionTypeAccessorKey, currentUserPermissionKey, permissionObjectAccessorKey] =
            this.getConditionAccessKeys(permissionJSON);
        const userPermissionArray = currentUser[currentUserPermissionTypeAccessorKey] as InternalPermissions[] | ResourcePermissions[];
        const userPermissionObject = userPermissionArray.find((userPermission: any) => {
            return userPermission[currentUserPermissionKey] === permissionJSON[permissionObjectAccessorKey];
        }) as { accessType: AccessLevel };

        if (!userPermissionObject) {
            return false;
        }

        return this.hasRequiredAccessLevel(permissionJSON, userPermissionObject.accessType.toLowerCase() as AccessLevel);
    }

    /**
     * Used to check if the user (an admin user) has access to a certain area
     * @param permissionJSON - an end permission (not array or arrays or array of objects) object
     * @param currentUser
     * @protected
     */
    protected hasAccessBasedOnAdminPermission(permissionJSON: PermissionJSON, currentUser: CurrentShijiUserInterface): boolean {
        if (permissionJSON.requiresSpecialAdminPermission) {
            const specialAdminPermission = permissionJSON.requiresSpecialAdminPermission;
            const index = currentUser.shijiPermissions.findIndex(
                (item) => item.shijiAccessZone === specialAdminPermission && item.isAuthorized,
            );

            if (index !== -1) {
                return true;
            }
        } else if (permissionJSON.allowAnyAdmin) {
            return true;
        }

        return false;
    }

    /**
     * Returns the keys that are used to access the various permission objects based on the permission type required
     * @param permissionObject
     * @private
     */
    private getConditionAccessKeys(
        permissionObject: PermissionJSON,
    ): ['internalPermissions', 'permissionAccessZone', 'name'] | ['resourcePermissions', 'name', 'resourceName'] {
        if (permissionObject.type === 'internal') {
            return ['internalPermissions', 'permissionAccessZone', 'name'];
        } else if (permissionObject.type === 'resource' || permissionObject.type === 'admin') {
            return ['resourcePermissions', 'name', 'resourceName'];
        }

        throw new ReferenceError('Permission type is neither internal nor resource but (or admin), instead, ' + permissionObject.type);
    }

    /**
     * Adds field names to the resource permissions object so it can more easily be compared later
     * @param currentUser
     * @param availableResources
     * @private
     */
    protected enrichUserResourcePermissionsObject(
        currentUser: CurrentSupplierUserInterface,
        availableResources?: { [id: string]: FeatureGroup },
    ): CurrentSupplierUserInterface {
        if (!currentUser.resourcePermissions || !availableResources || Object.keys(availableResources).length === 0) {
            return currentUser;
        }

        currentUser.resourcePermissions.forEach((resourcePermission) => {
            if (availableResources && availableResources[resourcePermission.resourceId]) {
                (resourcePermission as any).name = availableResources[resourcePermission.resourceId].fieldName;
            }
        });

        return currentUser;
    }

    /**
     * Checks if the user has the required access level
     * (ex. required is read access and user has edit access; edit > read => true)
     * @param permissionObject
     * @param accessLevel
     * @protected
     */
    protected hasRequiredAccessLevel(permissionObject: PermissionJSON, accessLevel: AccessLevel): boolean {
        const minimumAccessLevel = PERMISSIONS_HIERARCHY.findIndex((level) => level === permissionObject.minLevel);
        const maximumAccessLevel = PERMISSIONS_HIERARCHY.findIndex((level) => level === permissionObject.maxLevel);
        const actualAccessLevel = PERMISSIONS_HIERARCHY.findIndex((level) => level === accessLevel);

        if (maximumAccessLevel !== -1) {
            return maximumAccessLevel >= actualAccessLevel && actualAccessLevel >= minimumAccessLevel;
        }

        return actualAccessLevel >= minimumAccessLevel;
    }
}
